<template>
	<div
		class="search-filter-container"
		:class="{ active: selectedOptionIds.length > 0 }"
	>
		<Dropdown
			v-model="selectedOptions"
			search
			multiple
			global
			pill
			small
			showOverlay
			focusFirst
			:options="optionsWithSelection"
			:loading="loading"
			:loadingMore="loadingMore"
			:searchPlaceholder="searchPlaceholder"
			:filterFn="filterFn"
			:hideOptions="!searchTerm && !selectedOptionIds.length"
			:hideEmptyState="!searchTerm"
			:hideCaret="selectedOptionIds.length > 1"
			:optionsClass="`search-filter-options ${optionsClass}`"
			@load-more="onLoadMore"
			@open="onOpenChange"
			@change="onChange"
			@search="onSearch"
		>
			<template #selected>
				<div class="search-filter-selected">
					<span class="search-filter-placeholder">
						<slot name="placeholder" v-bind="{ items: selectedOptions }" />
					</span>
					<span
						class="search-filter-selected-more"
						v-if="selectedOptions.length > 1"
						v-text="`+${selectedOptions.length - 1}`"
					/>
				</div>
			</template>

			<template v-slot="{ option }">
				<slot v-bind="{ option }" />
			</template>

			<template #options-bottom>
				<slot name="options-bottom" />
				<div
					class="search-filter-clear-all"
					@click.stop="onClearAll"
					v-if="selectedOptionIds.length > 0"
				>
					{{ t('admin_groups_clear_selection_button') }}
				</div>
			</template>
		</Dropdown>
	</div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { Dropdown } from '@/components/common'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const model = defineModel({ required: true })

const props = defineProps({
	options: Array,
	loading: Boolean,
	loadingMore: Boolean,
	filterFn: Function,
	searchPlaceholder: String,
	optionsClass: {
		type: String,
		default: '',
	},
})
const emit = defineEmits(['open', 'change', 'search', 'clear'])
const selectedOptionIds = ref(model.value || [])
const searchTerm = ref(null)
const selectedOptions = ref(
	model.value?.length
		? model.value.map((id) => props.options.find((o) => o.id === id) || {})
		: []
)

const optionsWithSelection = computed(() => {
	return props.options.map((o) => {
		o.selected = selectedOptionIds.value.some((id) => id === o.id)
		return o
	})
})

watch(model, (selectedIds) => {
	// if model changes, update the internal selectedOptionIds and selectedOptions lists
	selectedOptionIds.value = selectedIds
	const options = selectedIds
		.map((id) => props.options.find((o) => o.id === id))
		.filter((x) => x)
	if (options.length) {
		selectedOptions.value = options
	}
})

watch(
	() => props.options,
	(options) => {
		// if options prop changes, update the internal selectedOptions list
		selectedOptions.value = selectedOptionIds.value.map(
			(id) => options.find((o) => o.id === id) || {}
		)
	}
)

watch(selectedOptions, (options) => {
	const newIds = options.map((o) => o.id)
	const oldIdsStr = model.value.slice().sort().join(',')
	const newIdsStr = newIds.slice().sort().join(',')
	// only update the model if it isn't already updated
	if (newIdsStr !== oldIdsStr) {
		model.value = newIds
	}
})

function onClearAll() {
	selectedOptions.value = []
	model.value = []
	emit('clear')
}

function onChange(values) {
	emit('change', values)
}

function onSearch(...args) {
	searchTerm.value = args[0] || null
	emit('search', ...args)
}

function onOpenChange(open) {
	emit('open', open)
}

function onLoadMore() {}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.search-filter-container {
	&.active {
		:deep(.dropdown-container) {
			.dropdown-selected {
				border-color: var(--blue-300) !important;
				background: var(--blue-300) !important;
				color: white !important;
			}
		}
		.search-filter-placeholder {
			@include fontFamily(800);
			color: var(--neutral-0);
		}
	}
	:deep(.dropdown-container) {
		&.hideCaret {
			.dropdown-selected {
				padding-right: 2px;
			}
		}
		.dropdown-selected {
			min-width: 56px;
			max-width: 200px;
			background: var(--neutral-0);
		}
	}
	.search-filter-selected {
		@include flex(4px);
		flex: 1;
		.search-filter-placeholder {
			@include ellipsis;
			flex: 1;
		}
		.search-filter-selected-more {
			@include flex-column-center(4px);
			justify-content: center;
			min-width: 25px;
			height: 25px;
			padding: 0 4px;
			background: white;
			color: var(--blue-300);
			border-radius: 20px;
			font-size: 12px;
			margin-right: 4px;
			flex-shrink: 0;
			font-family: Avenir-Heavy;
		}
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';

.search-filter-options {
	$height: 400px;
	margin-top: 9px !important;
	width: 400px !important;
	&.open {
		max-height: $height !important;
	}
	&.scrollable {
		.scroller {
			max-height: calc(#{$height} - 45px) !important;
		}
	}
	&.empty {
		&:not(.searching) {
			.dropdown-search {
				.pronto-input {
					border: 0 !important;
				}
			}
		}
	}
	.dropdown-options {
		padding: 0 !important;
		overflow: hidden !important;
		.dropdown-option {
			width: 100%;
			&.no-hover {
				padding: 0 !important;
			}
			.pronto-checkbox {
				font-size: 18px;
				.pronto-checkbox-icon {
					width: 1em; // override
				}
			}
		}
	}

	.dropdown-search {
		.pronto-input {
			border-bottom: none !important;
		}
	}
	.search-filter-clear-all {
		@include flex;
		flex-shrink: 0;
		height: 40px;
		padding: 0 16px;
		color: var(--blue-300);
		cursor: pointer;
		font-size: 15px;
		border-top: 1px solid var(--neutral-200);
	}
}
</style>
